import { SourcingChannelDetails } from 'api-clients/monolith/models/SourcingChannelDetails';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  buildChannelDefaults,
  getChannelDefaults,
} from '@/components/pages/job-posting/utils/channel-defaults';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { useJobPostingContext } from '@/contexts/job-posting-context';

import { useChannelDescription } from '../use-channel-description';
import { useUnsupportedOpenings } from '../use-unsupported-openings';
import { CardLayout } from './card-layout';
import { MutedMessage } from './muted-message';

interface DirectChannelCardProps {
  channel: SourcingChannelDetails;
  isIndeedIntegrationDisabled?: boolean;
  isFree?: boolean;
}

export function DirectChannelCard({
  channel,
  isIndeedIntegrationDisabled = false,
  isFree = false,
}: DirectChannelCardProps) {
  const intl = useIntl();
  const { getDescription, getTitle } = useChannelDescription();
  const { hookForm } = useJobPostingContext();
  const { getValues, setValue } = hookForm;

  const displayTitle = getTitle(channel.slug, channel.name);
  const rawDescription = getDescription(channel.slug);
  const channelDefaults = getChannelDefaults(channel.slug);
  const { allOpeningsUnsupported, message: unsupportedMessage } =
    useUnsupportedOpenings(channel.unsupported_openings, channel.name);

  const disabled =
    isIndeedIntegrationDisabled ||
    !channel.is_publishable ||
    allOpeningsUnsupported;

  /**
   * Override precedence: integration > Assist-managed > unsupported openings > raw description.
   * The first matching reason wins; if none apply we show the channel's normal blurb (which may itself be null).
   */
  let description: React.ReactNode | null;
  if (isIndeedIntegrationDisabled) {
    description = (
      <MutedMessage>
        <FormattedMessage
          defaultMessage="Indeed integration is not enabled for this account."
          description="Message to show when Indeed integration is not enabled for the account"
        />
      </MutedMessage>
    );
  } else if (channel.managed_by_assist && !channel.is_publishable) {
    description = (
      <MutedMessage>
        <FormattedMessage
          defaultMessage="This channel is managed by Fountain Assist."
          description="Message to show when a channel is managed by Fountain Assist"
        />
      </MutedMessage>
    );
  } else if (unsupportedMessage) {
    description = <MutedMessage>{unsupportedMessage}</MutedMessage>;
  } else {
    description = rawDescription;
  }

  /**
   * Add the channel to `channels` form state. Selecting seeds defaults for
   * opening budgets and the per-channel `unsupportedOpenings` list.
   * When the slug is `'pool'`, `buildChannelDefaults` automatically populates
   * `poolDetails` — no special-casing needed at the call site.
   */
  const handleSelect = () => {
    if (allOpeningsUnsupported) return;

    const currentChannels = getValues('channels') ?? {};

    // Return if the channel is already selected
    if (Object.keys(currentChannels).includes(channel.slug)) return;

    setValue('channels', {
      ...currentChannels,
      [channel.slug]: buildChannelDefaults({
        channelSlug: channel.slug,
        channelName: channel.name,
        displayTitle,
        openingIds: [],
        unsupportedOpenings: channel.unsupported_openings ?? [],
        isFree,
      }),
    });
  };

  return (
    <CardLayout
      channelKey={channel.slug}
      channelSlug={channel.slug}
      icon={<ChannelIcon channelSlug={channel.slug} variant="large" />}
      displayTitle={displayTitle}
      recommendedDuration={channelDefaults.duration}
      minDuration={channelDefaults.minDuration}
      maxDuration={channelDefaults.maxDuration}
      recommendedBudget={channelDefaults.budget}
      minBudget={channelDefaults.minBudget}
      channelType={intl.formatMessage({
        defaultMessage: 'Job Board',
        description: 'The type of the channel for job posting',
      })}
      description={description}
      infoSheet={{
        icon: (
          <ChannelIcon channelSlug={channel.slug} variant="large" size="lg" />
        ),
        description: rawDescription,
      }}
      disabled={disabled}
      onSelect={handleSelect}
      isFree={isFree}
    />
  );
}
